// เคสนัดใหม่ประจำวัน (/reports/newcase) — the pure shaping behind the report.
//
// The Xano endpoint (`/report/newcase_daily`, api 649) returns two flat lists,
// `patients` and `appointments`. Everything that turns those into report rows
// and groups lives here rather than in the page, because the definition of
// "a new case" is a business rule with edges worth pinning down in tests:
//
//   • ONE ROW = ONE NEW HN. A patient with three appointments is still one
//     case; the extras are counted, never listed as separate cases, or the
//     report would double-count the thing it exists to count.
//   • THE appointment is the EARLIEST one. Measured 2026-09-07 against live
//     data: a new HN averages 1.37 appointments, and the ต่อเนื่อง ones are
//     always the LATER rows — the first appointment is the acquisition
//     channel, which is exactly what ที่มา is being grouped on.
//   • A NEW HN WITH NO APPOINTMENT IS STILL A CASE. It is the gap the report
//     exists to surface (someone opened a chart and booked nothing), so it
//     gets its own group that sorts LAST however big it is, rather than being
//     dropped the way the old Bubble chart drops it.
import { relabelBranch } from './asset.js'

export const NO_APPT = '__noappt__'
export const NO_APPT_LABEL = 'ยังไม่มีนัด'
export const UNSET = 'ไม่ระบุ'

const BKK_OFFSET_MS = 7 * 60 * 60 * 1000

// epoch ms → the Bangkok calendar day / wall clock. Shifting the instant by +7h
// and reading the UTC fields keeps this correct regardless of the viewer's
// timezone — the report is read against clinic days, not the browser's.
export const bkkDayISO = (ms) => new Date(Number(ms) + BKK_OFFSET_MS).toISOString().slice(0, 10)
export const bkkTime = (ms) => new Date(Number(ms) + BKK_OFFSET_MS).toISOString().slice(11, 16)

// The clinic's TODAY, in Bangkok — not the browser's. These two differ for
// seven hours of every UTC day, and for a viewer abroad they can differ all day.
// The report's window is resolved in Bangkok server-side, so its default range
// and its "no future dates" cap have to be the Bangkok day or the picker can
// offer a day the query will not accept.
export const bkkTodayISO = () => bkkDayISO(Date.now())

// 'YYYY-MM-DD' → 'd/m/yy'. Gregorian, matching the other inspection reports —
// these are read against rosters and payroll periods, not shown to patients.
export function fmtDay(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-')
  if (!y || !m || !d) return String(iso)
  return `${Number(d)}/${Number(m)}/${y.slice(2)}`
}

/**
 * Join the two upstream lists into one row per new HN.
 *
 * @param patients      rows from `patients` (id, HN, fullname, branch_id, created_at, contact…)
 * @param appointments  rows from `appointments`, already sorted date asc / id asc upstream
 * @param types         appointmentList catalog: id → { type, detail }
 * @param branches      branch catalog: id → name
 */
export function buildRows(patients, appointments, types = {}, branches = {}) {
  const byPatient = new Map()
  for (const a of appointments || []) {
    const pid = String(a?.patient_id ?? '')
    if (!pid || pid === '0') continue
    if (!byPatient.has(pid)) byPatient.set(pid, [])
    byPatient.get(pid).push(a)
  }

  return (patients || []).map((p) => {
    const pid = String(p?.id ?? '')
    // Sorted upstream by date asc, id asc — but re-sort defensively so the
    // "first appointment" rule does not depend on the transport preserving it.
    // A null/empty date sorts LAST (an undated row is not evidence of the
    // earliest booking), which is also what Postgres NULLS LAST gives us.
    const appts = [...(byPatient.get(pid) || [])].sort((a, b) => {
      const da = a?.date || '', db = b?.date || ''
      if (da !== db) {
        if (!da) return 1
        if (!db) return -1
        return da < db ? -1 : 1
      }
      return (Number(a?.id) || 0) - (Number(b?.id) || 0)
    })
    const a = appts[0] || null
    const t = a ? types[String(a.appointmentlist_id ?? '')] : null

    const branchId = String(p?.branch_id ?? '')
    const branchName = branches[branchId]
      ? relabelBranch(branches[branchId])
      : (branchId && branchId !== '0' ? `สาขา #${branchId}` : UNSET)

    return {
      patientId: p?.id ?? null,
      hn: String(p?.HN || '').trim(),
      name: String(p?.fullname || '').trim() || '—',
      nickname: String(p?.nickname || '').trim(),
      phone: String(p?.contact?.phone || '').trim(),
      patientStatus: String(p?.status || '').trim(),
      branchId,
      branchName,
      createdMs: Number(p?.created_at) || 0,
      createdDay: p?.created_at ? bkkDayISO(p.created_at) : '',
      createdTime: p?.created_at ? bkkTime(p.created_at) : '',

      hasAppt: !!a,
      apptId: a?.id ?? null,
      apptDate: a?.date || '',
      apptStatus: a?.status || '',
      apptFrom: a ? (String(a.from || '').trim() || UNSET) : NO_APPT_LABEL,
      apptTypeId: a ? String(a.appointmentlist_id ?? '') : '',
      typeMain: a ? (t?.type || `#${a.appointmentlist_id || '—'}`) : NO_APPT_LABEL,
      typeDetail: a && t?.detail && t.detail !== t?.type ? t.detail : '',
      apptDetails: String(a?.details || '').trim(),
      extraAppts: Math.max(0, appts.length - 1),
    }
  })
}

// The four grouping axes. `key` is what rows are bucketed on, `label` is what
// the bucket is called. They differ for branch (id vs name) and date (ISO vs
// display) so that sorting stays on the stable value.
export const GROUPERS = {
  type:   { noun: 'ประเภทนัดหมาย', key: (r) => (r.hasAppt ? r.typeMain : NO_APPT), label: (r) => r.typeMain },
  from:   { noun: 'ที่มา',          key: (r) => (r.hasAppt ? r.apptFrom : NO_APPT), label: (r) => r.apptFrom },
  branch: { noun: 'สาขา',          key: (r) => r.branchId || UNSET,                 label: (r) => r.branchName },
  date:   { noun: 'วันที่',         key: (r) => r.createdDay || UNSET,               label: (r) => fmtDay(r.createdDay) },
}

/** Group rows on one axis → [{ key, label, count, rows }]. */
export function pivot(rows, dim) {
  const g = GROUPERS[dim]
  if (!g) return []
  const m = new Map()
  for (const r of rows || []) {
    const key = g.key(r)
    if (!m.has(key)) m.set(key, { key, label: g.label(r), rows: [] })
    m.get(key).rows.push(r)
  }
  const out = [...m.values()].map((x) => ({ ...x, count: x.rows.length }))

  // A date axis reads chronologically — ranking days by size would make a
  // trend unreadable. Every other axis ranks biggest-first.
  if (dim === 'date') return out.sort((a, b) => String(a.key).localeCompare(String(b.key)))

  return out.sort((a, b) => {
    // ยังไม่มีนัด is a gap, not a category: pin it last however big it gets.
    if (a.key === NO_APPT) return 1
    if (b.key === NO_APPT) return -1
    return b.count - a.count || String(a.label).localeCompare(String(b.label), 'th')
  })
}

/* ── the ประเภทนัดหมาย filter ───────────────────────────────────────────────
 * TWO levels, because `appointmentList` is a two-column catalog: `type` is the
 * family (GP · Ortho · Prosth) and `detail` is the actual procedure. Grouping
 * stays at the family level — that is the readable summary — but the FILTER can
 * go all the way down to one catalogue row.
 *
 * The filter value is a tagged string so the two levels cannot be confused:
 *   ''            every case
 *   't:<type>'    a whole family
 *   'i:<id>'      one appointmentList row
 *   NO_APPT       only the ยังไม่มีนัด gap
 * A bare untagged string is read as a family name, so a value persisted by an
 * older build keeps working instead of silently matching nothing.
 */
export const TYPE_ALL = ''
export const typeFamilyValue = (type) => `t:${type}`
export const typeItemValue = (id) => `i:${id}`

/**
 * Build the grouped option list from the rows actually in the window — never
 * the whole catalog, which is hundreds of rows most of which never appear.
 * Families rank by size, and so do the entries inside each one.
 * → [{ type, count, value, items: [{ id, label, count, value }] }]
 */
export function typeOptions(rows) {
  const fam = new Map()
  for (const r of rows || []) {
    if (!r.hasAppt) continue
    if (!fam.has(r.typeMain)) fam.set(r.typeMain, { type: r.typeMain, count: 0, items: new Map() })
    const f = fam.get(r.typeMain)
    f.count += 1
    const id = r.apptTypeId || ''
    if (!f.items.has(id)) {
      // typeDetail is blanked when it merely repeats the family name, so fall
      // back to the family — that row is "GP with no more specific detail".
      f.items.set(id, { id, label: r.typeDetail || r.typeMain, count: 0, value: typeItemValue(id) })
    }
    f.items.get(id).count += 1
  }
  return [...fam.values()]
    .map((f) => ({
      type: f.type,
      count: f.count,
      value: typeFamilyValue(f.type),
      items: [...f.items.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'th')),
    }))
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type, 'th'))
}

/** Does one row satisfy a tagged ประเภทนัดหมาย filter value? */
export function matchesType(r, value) {
  if (!value) return true
  if (value === NO_APPT) return !r.hasAppt
  if (!r.hasAppt) return false
  if (value.startsWith('t:')) return r.typeMain === value.slice(2)
  if (value.startsWith('i:')) return r.apptTypeId === value.slice(2)
  return r.typeMain === value // untagged legacy value
}

/** Client-side filters. '' = no filter; see matchesType for the type value. */
export function filterRows(rows, { branchId = '', type = '' } = {}) {
  return (rows || []).filter((r) => (
    (!branchId || r.branchId === branchId) && matchesType(r, type)
  ))
}
